/**
 * ELO Rating System for Multiplayer Free-For-All
 * Supports ties, top-50%/bottom-50% guarantees, and smurf/underdog protection.
 *
 * Rules:
 *   - Top 50% finishers always gain rating (minimum placement > 0.5)
 *   - Bottom 50% finishers always lose rating (maximum placement < 0.5)
 *   - Exact middle position in odd-player games = 0 change
 *   - Tied players share the rating pool equally
 *   - K-factor scales down with experience (new players learn fast)
 *   - Skill-gap multiplier prevents smurf farming and rewards underdogs
 */

/**
 * Raw placement score for a single discrete position (no ties).
 * Returns 1.0 for 1st place, 0.0 for last place.
 *
 * For even player counts: positions 1..n/2 are top half (score > 0.5),
 *   positions n/2+1..n are bottom half (score < 0.5).
 * For odd player counts: exact middle position = 0.5.
 */
function getPlacementScore(position, totalPlayers) {
  if (totalPlayers % 2 === 1) {
    // Odd player count
    const middlePosition = Math.ceil(totalPlayers / 2);
    if (position === middlePosition) {
      return 0.5; // Exact middle → neutral
    }
    if (position < middlePosition) {
      // Top half: 1.0 (1st) down to 0.51
      const topHalfSize = middlePosition - 1;
      return topHalfSize === 0 ? 1.0 : 1.0 - ((position - 1) / topHalfSize) * 0.49;
    } else {
      // Bottom half: 0.49 down to 0.0 (last place)
      const bottomPosition = position - middlePosition; // 1-based offset from middle
      const bottomHalfSize = totalPlayers - middlePosition;
      return 0.49 - (bottomPosition / bottomHalfSize) * 0.49;
    }
  } else {
    // Even player count: top half = positions 1..n/2, bottom half = n/2+1..n
    const halfSize = totalPlayers / 2;
    if (position <= halfSize) {
      // Top half: 1.0 (1st) → 0.51 (last top-half)
      return halfSize === 1 ? 1.0 : 1.0 - ((position - 1) / (halfSize - 1)) * 0.49;
    } else {
      // Bottom half: 0.49 (first bottom) → 0.0 (last place)
      const bottomIndex = position - halfSize - 1; // 0-based within bottom half
      return halfSize === 1 ? 0.0 : 0.49 - (bottomIndex / (halfSize - 1)) * 0.49;
    }
  }
}

/**
 * Placement score with tie support.
 * Tied players receive the average of all positions they collectively occupy.
 * Guarantees:
 *   - If all tied positions are in top half → result > 0.5
 *   - If all tied positions are in bottom half → result < 0.5
 *   - If tie spans the boundary → natural average (may be ≈0.5)
 */
function getPlacementScoreWithTies(myScore, allScores, totalPlayers) {
  const sortedScores = [...allScores].sort((a, b) => b - a);

  const playersWithMyScore = allScores.filter(s => s === myScore).length;
  const firstPosition = sortedScores.indexOf(myScore) + 1;
  const lastPosition = firstPosition + playersWithMyScore - 1;

  let sumPlacementScores = 0;
  for (let pos = firstPosition; pos <= lastPosition; pos++) {
    sumPlacementScores += getPlacementScore(pos, totalPlayers);
  }
  let avgPlacement = sumPlacementScores / playersWithMyScore;

  // Determine top/bottom boundary for the guarantee check
  // Even: top = 1..n/2, bottom = n/2+1..n
  // Odd:  top = 1..mid-1, middle = mid, bottom = mid+1..n
  const isEven = totalPlayers % 2 === 0;
  const topHalfLastPos  = isEven ? totalPlayers / 2 : Math.ceil(totalPlayers / 2) - 1;
  const bottomHalfFirst = isEven ? totalPlayers / 2 + 1 : Math.ceil(totalPlayers / 2) + 1;

  if (lastPosition <= topHalfLastPos) {
    // All tied positions in top half → guarantee a gain
    avgPlacement = Math.max(avgPlacement, 0.51);
  } else if (firstPosition >= bottomHalfFirst) {
    // All tied positions in bottom half → guarantee a loss
    avgPlacement = Math.min(avgPlacement, 0.49);
  }
  // else: tie spans the boundary or includes mid → use natural average

  return avgPlacement;
}

/**
 * Expected score: probability-weighted expectation of finishing above each opponent.
 * 0.5 = balanced match, 0.8 = heavy favourite, 0.2 = clear underdog.
 */
function getExpectedScore(myRating, opponentRatings) {
  if (opponentRatings.length === 0) return 0.5;

  let totalExpectation = 0;
  for (const oppRating of opponentRatings) {
    const ratingDiff = oppRating - myRating;
    const probability = 1 / (1 + Math.pow(10, ratingDiff / 400));
    totalExpectation += probability;
  }

  return totalExpectation / opponentRatings.length;
}

/**
 * K-factor controls volatility (how many points are on the line per game).
 * New players experience larger swings to calibrate quickly.
 */
function getKFactor(gamesPlayed) {
  if (gamesPlayed < 10) return 40;  // Fresh account – calibration period
  if (gamesPlayed < 50) return 28;  // Regular player
  return 20;                         // Veteran – stable rating
}

/**
 * Skill-gap multiplier: discourages rating farming in one-sided matches.
 * Directional behaviour (by result quality):
 *   - Favourite over weaker lobby:
 *       good result  -> dampened gains
 *       bad result   -> amplified losses
 *   - Underdog vs stronger lobby:
 *       good result  -> amplified gains
 *       bad result   -> dampened losses
 */
function getSkillGapMultiplier(myRating, avgOpponentRating, performanceDelta) {
  const gap = myRating - avgOpponentRating;
  const didOverperform = performanceDelta >= 0;

  if (gap > 300) return didOverperform ? 0.25 : 1.75;
  if (gap > 150) return didOverperform ? 0.50 : 1.50;
  if (gap < -150) return didOverperform ? 1.50 : 0.75;
  return 1.0;                    // Fair match
}

/**
 * Player-count multiplier: encourages larger lobbies.
 *   5 or fewer players → 1.0 (baseline)
 *   6 players → 1.1
 *   7 players → 1.2
 *   8 players → 1.3
 */
export function getPlayerCountMultiplier(totalPlayers) {
  if (totalPlayers <= 5) return 1.0;
  if (totalPlayers === 6) return 1.1;
  if (totalPlayers === 7) return 1.2;
  return 1.3; // 8+
}

export const AI_TAKEOVER_RATING_RULES = Object.freeze({
  maxReducedGainFactor: 0,
  maxAmplifiedLossFactor: 2,
});

const normalizeNonNegativeInteger = (value) => {
  const normalized = Number(value);
  if (!Number.isFinite(normalized)) return 0;
  return Math.max(0, Math.trunc(normalized));
};

export const getAITakeoverRatingImpact = (aiControlledMoves = 0, totalSeatMoves = null) => {
  const normalizedAIMoves = normalizeNonNegativeInteger(aiControlledMoves);
  const normalizedTotalMovesCandidate = totalSeatMoves == null
    ? normalizedAIMoves
    : normalizeNonNegativeInteger(totalSeatMoves);
  const normalizedTotalMoves = Math.max(normalizedAIMoves, normalizedTotalMovesCandidate);

  if (normalizedAIMoves === 0 || normalizedTotalMoves === 0) {
    return {
      aiControlledMoves: normalizedAIMoves,
      totalSeatMoves: normalizedTotalMoves,
      humanControlledMoves: normalizedTotalMoves,
      aiShare: 0,
      humanShare: 1,
      gainFactor: 1,
      lossFactor: 1,
      gainReductionPercent: 0,
      lossIncreasePercent: 0,
    };
  }

  const aiShare = Math.min(1, normalizedAIMoves / normalizedTotalMoves);
  const humanShare = Math.max(0, 1 - aiShare);

  return {
    aiControlledMoves: normalizedAIMoves,
    totalSeatMoves: normalizedTotalMoves,
    humanControlledMoves: Math.max(0, normalizedTotalMoves - normalizedAIMoves),
    aiShare,
    humanShare,
    gainFactor: humanShare,
    lossFactor: 1 + aiShare,
    gainReductionPercent: Math.round(aiShare * 100),
    lossIncreasePercent: Math.round(aiShare * 100),
  };
};

export const applyAITakeoverRatingAdjustment = (change, aiControlledMoves = 0, totalSeatMoves = null) => {
  if (!Number.isFinite(change) || change === 0) return change;

  const impact = getAITakeoverRatingImpact(aiControlledMoves, totalSeatMoves);

  if (impact.aiControlledMoves === 0) return change;
  if (change > 0) {
    return Math.round(change * Math.max(AI_TAKEOVER_RATING_RULES.maxReducedGainFactor, impact.gainFactor));
  }
  return Math.round(change * Math.min(AI_TAKEOVER_RATING_RULES.maxAmplifiedLossFactor, impact.lossFactor));
};

/**
 * Calculate ELO rating change for one player after a free-for-all match.
 *
 * @param {number}   myRating         - Current rating of the player
 * @param {number}   myScore          - Game score (points) of the player
 * @param {number[]} allScores        - Array of ALL player scores (including this player)
 * @param {number[]} opponentRatings  - Array of opponent ratings (excluding this player)
 * @param {number}   gamesPlayed      - Total games played before this game
 * @returns {number} Integer rating change (positive = gain, negative = loss)
 */
export const calculateRatingChange = (
  myRating,
  myScore,
  allScores,
  opponentRatings,
  gamesPlayed
) => {
  const totalPlayers = allScores.length;

  const placementScore = getPlacementScoreWithTies(myScore, allScores, totalPlayers);
  const expectedScore = getExpectedScore(myRating, opponentRatings);
  const K = getKFactor(gamesPlayed);
  const performanceDelta = placementScore - expectedScore;

  const avgOpponentRating =
    opponentRatings.reduce((sum, r) => sum + r, 0) / opponentRatings.length;
  const skillMultiplier = getSkillGapMultiplier(myRating, avgOpponentRating, performanceDelta);
  const playerCountMultiplier = getPlayerCountMultiplier(totalPlayers);

  let change = K * performanceDelta * skillMultiplier * playerCountMultiplier;

  // Top-50% guarantee: finishing in the upper half should never cost rating.
  // Bottom-50% guarantee: finishing in the lower half should never gain rating.
  if (placementScore > 0.5) {
    change = Math.max(change, 0);
  } else if (placementScore < 0.5) {
    change = Math.min(change, 0);
  }

  return Math.round(change);
};

/**
 * Compute display places for a sorted list of scores, accounting for ties.
 * e.g. [27, 26, 24, 24, 22] → [1, 2, 3, 3, 5]
 *
 * @param {number[]} sortedScores - Scores sorted descending (highest first)
 * @returns {number[]} Array of 1-based place numbers, same length as input
 */
export const computePlaces = (sortedScores) => {
  return sortedScores.map(score =>
    sortedScores.filter(s => s > score).length + 1
  );
};

export const RANK_TIERS = Object.freeze([
  Object.freeze({ minRating: 2100, name: 'Master', icon: '👑', svg: '/icons/rank_master.svg', color: '#c084fc' }),
  Object.freeze({ minRating: 1800, name: 'Diamond', icon: '💎', svg: '/icons/rank_diamond.svg', color: '#7dd3fc' }),
  Object.freeze({ minRating: 1500, name: 'Platinum', icon: '🥇', svg: '/icons/rank_platinum.svg', color: '#5eead4' }),
  Object.freeze({ minRating: 1200, name: 'Gold', icon: '🥈', svg: '/icons/rank_gold.svg', color: '#fbbf24' }),
  Object.freeze({ minRating: 900, name: 'Silver', icon: '🥉', svg: '/icons/rank_silver.svg', color: '#cbd5e1' }),
  Object.freeze({ minRating: 0, name: 'Bronze', icon: '🪨', svg: '/icons/rank_bronze.svg', color: '#c47a3a' }),
]);

/**
 * Returns the rank tier object for a given ELO rating.
 * Used as the single source of truth for rank names, icons, SVGs and colors.
 */
export const getRankTier = (rating) => {
  const tier = RANK_TIERS.find(candidate => rating >= candidate.minRating) || RANK_TIERS[RANK_TIERS.length - 1];
  const { minRating: _minRating, ...publicTier } = tier;
  return publicTier;
};
